import CompanyService from '../services/CompanyService'
import { PaUsageValues } from './api/Company'
import { Utils } from '../util/utils';
import firebase from 'firebase/compat/app'
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import moment from 'moment-timezone'
import pickBy from "lodash-es/pickBy"
import store from '../store'

export enum CustomerType {
  DIRECT = 'direct',
  AGENCY = 'agency',
}

export enum Source {
  CLIO = 'clio',
  QBO = 'qbo',
  WEBSITE = 'website',
  DRCHRONO = 'drchrono',
  IOS = 'ios',
  ANDROID = 'android',
}

export interface APIKey {
  [key: string]: string
}

export enum LinkOpenMode {
  IFRAME = 'iframe',
  NEW_TAB = 'newTab',
}

export interface AllowBetaAccess {
  workflow: boolean
  saasMode: boolean
  isvMode: boolean
  emailRebilling: boolean
  tipalti: boolean,
  proPlus: boolean
  mobileapp: boolean
  metaWhatsapp: boolean
}

export enum MarketplaceSandBoxRenewalStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}
export interface MarketplaceSandbox {
  renewal_request_status?: MarketplaceSandBoxRenewalStatus
  renewal_requested_on?: any
  renewal_processed_on?: any
  renewal_requested_by?: string
}

export interface ISaasSettings {
  agency_dashboard_visible_to: 'admin' | 'individual'
  stripe_connect_initiated_by: string
}

// export interface IIsvSettings {
//   isv_mode: boolean
// }
export interface WhitelabelAppLinks {
  android: string,
  ios: string
}

export enum PauseSubscriptionStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export interface EndTrial {
  trial_end_req_by: string
  trial_ended_on: Date
}

export enum AgencyproPlusAddonPlan {
  Monthly = 'agencypro_plus_addon_monthly',
  Annual = 'agencypro_plus_addon_annual',
}

export interface AgencyProAddOn {
  is_active: Boolean
  agency_pro_addon_subscription_id: string
  agency_pro_addon_active_plan: AgencyproPlusAddonPlan
}

export interface BillingInfo {
  first_trial_extension_processed_on?: any
  first_trial_extension_reason?: string
  second_trial_extension_processed_on?: any
  second_trial_extension_reason?: string
  pause_subscription_requested_on?: any
  pause_subscription_reason?: string
  pause_subscription_status?: PauseSubscriptionStatus
  pause_subscription_req_processed_on?: any
  pause_subscription_req_by?: string
  end_trial_early: EndTrial
  agency_pro_addon: AgencyProAddOn
  first_payment_date?: any
  has_invalid_billing_address: boolean,
}


export interface YextSaasOffer{
  plan?:string
}

export interface YextAgencySettings{
  agency_price?:number,
  hl_price?:number,
  product_id?:string,
  subscriber_count?:number,
  subscriptions?:string[]
  subscribed_locations?:[{ [key: string]: any }]
  prices?:[{ [key: string]: any }]
  saas_offer?:YextSaasOffer
}

export interface BlockFeatures {
  lc_email?: boolean
}

export interface IonboardingInfo {
  location?: boolean
  conversationDemo?: boolean
}

export interface SaasSettings {
  agencyDashboardVisibleTo?: string
  stripeConnectInitiatedBy?: string
  customEmailTemplateLocationId?: string
  welcomeEmailTemplateId?: string
  pauseOnCreation?: boolean
  enablePhoneVerification?: boolean
  upgradeLocationPermissions?: boolean
}

export interface ActiveStatusDetails {
  is_active: boolean
  updated_by: string
  updated_at: Date
  reason: string
  is_temporarily_suspended?: boolean
}

export interface ICompany {
  promoterId?: number
  onboardingInfo?: IonboardingInfo,
  upgradeAlertDismissed?: moment.Moment,
  experienceNpsEnabled?: boolean,
  experienceGuidesEnabled?: boolean,
  experienceReportsEnabled?: boolean,
  betaFeaturesEnabled?: boolean,
  saasSettings?:SaasSettings,
  referralId?: string,
  affiliatePixelId?:string,
  defaultEmailService?: string,
  paUsage?: PaUsageValues[],
  elizaFirstLocationEnabled?: boolean,
  sideBarNotificationType?: string,
  upgradeEnabledForClients?: boolean,
  cancelEnabledForClients?: boolean,
  autoSuspendEnabled?: boolean
  yextAgencyPrice?: number
  yextSaasOffer?: YextSaasOffer
}


type Status =
  | 'active'
  | 'active_trial'
  | 'active_trial-extension'
  | 'active_test-agency'
  | 'active_special'
  | 'active_overused'
  | 'active_failed-payment'
  | 'inactive_failed-payment'
  | 'inactive_paused'
  | 'inactive_cancelled'
  | 'inactive'
  | ''

export default class Company {
  public static collectionRef(): firebase.firestore.CollectionReference {
    return firebase.firestore().collection('companies')
  }

  public static getById(companyId: string): Promise<Company> {
    return new Promise((resolve, reject) => {
      Company.collectionRef()
        .doc(companyId)
        .get()
        .then((snapshot: firebase.firestore.DocumentSnapshot) => {
          if (snapshot.exists) resolve(new Company(snapshot))
          resolve()
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        })
    })
  }

  public static getStreamById(
    id: string
  ): firebase.firestore.DocumentReference {
    return Company.collectionRef().doc(id)
  }

  public save() {
    const _self = this
    if (!_self.customerType) {
      throw new Error('Problem while updating company')
    }

    return new Promise((resolve, reject) => {
      _self.dateUpdated = moment()
      _self._ref.set(this.data).then(_ => {
        resolve(_self)
      })
    })
  }

  public static async saveV2(companyId: string, data: ICompany) {
    try {
      const response = await CompanyService.update(companyId, data)
      await store.dispatch('company/update', response.company)
    } catch (error) {
      console.error(`error in saveV2 of companies model`, { error })
      throw error
    }
  }

  public static async updateReselling(companyId: string, data: ICompany) {
    try {
      const response = await CompanyService.updateReselling(companyId, data)
      await store.dispatch('company/update', response.company)
    } catch (error) {
      console.error(`Error while saving Reselling Agency Data: `, error?.message)
    }
  }

  public update(data) {
    const _self = this
    if (!_self.customerType) {
      throw new Error('Problem while updating company')
    }

    return new Promise((resolve, reject) => {
      _self.dateUpdated = moment()
      _self._ref.update(data).then(_ => {
        resolve(_self)
      })
    })
  }

  private _id: string
  private _data: firebase.firestore.DocumentData
  private _ref: firebase.firestore.DocumentReference

  constructor(params: { [key: string]: any } | firebase.firestore.DocumentSnapshot) {
    if (params) {
      if (params instanceof firebase.firestore.DocumentSnapshot) {
        this._id = params.id
        this._ref = params.ref
        this._data = params.data() || {}
      } else {
        this._id = params.id
        this._ref = Company.collectionRef().doc(params.id)
        this._data = params || {}
      }
    }

    if (['Etc/Greenwich', 'GMT'].includes(this.timezone)) {
      this.timezone = 'UTC'
    }
  }

  get id(): string {
    return this._id
  }

  get ref(): firebase.firestore.DocumentReference {
    return this._ref
  }

  get data(): { [key: string]: any } {
    return pickBy(this._data, v => v !== null && v !== undefined)
  }

  get secureData(): { [key: string]: any } {
    const data = pickBy(
      this._data,
      (v, key) =>
        v !== null &&
        v !== undefined &&
        !['api_keys', 'api_key_titles'].includes(key)
    )

    let dateAdded = 1000
    let dateUpdated = 1000

    if(this._data['date_added'] && isNumber(this._data['date_added'])) {
      dateAdded = this._data['date_added']
    } else if (this._data['date_added']) {
      dateAdded = this._data['date_added'].toMillis()
    }

    if(this._data['date_updated'] && isNumber(this._data['date_updated'])) {
      dateUpdated = this._data['date_updated']
    } else if (this._data['date_updated']) {
      dateUpdated = this._data['date_updated'].toMillis()
    }

    data.date_added = dateAdded
    data.date_updated = dateUpdated
    return data
  }

  get dateAdded(): moment.Moment {
    let dateAdded = 1000

    if(this._data['date_added'] && isNumber(this._data['date_added'])) {
      dateAdded = this._data['date_added']
    } else if (this._data['date_added']) {
      dateAdded = this._data['date_added'].toMillis()
    }

    return moment(dateAdded)
  }

  set dateAdded(dateAdded: moment.Moment) {
    this._data.date_added = dateAdded.toDate()
  }

  get dateUpdated(): moment.Moment {
    let dateUpdated = 1000

    if(this._data['date_updated'] && isNumber(this._data['date_updated'])) {
      dateUpdated = this._data['date_updated']
    } else if (this._data['date_updated']) {
      dateUpdated = this._data['date_updated'].toMillis()
    }

    return moment(dateUpdated)
  }

  set dateUpdated(dateUpdated: moment.Moment) {
    this._data.date_updated = dateUpdated.toDate()
  }

  /**
   * Whether new calendar feature (Calendar v3) is turned on or not
   * If so, then agency admin has access to turn on or off isCalendarV3On flag for agency's locations
   */
  get isCalendarV3On(): boolean {
    return true // Now allowing v3 feature to all agencies // TODO: Remove flag later across the app
  }

  set isCalendarV3On(value: boolean) {
    this._data.is_calendar_v3_on = value
  }

  get dirty(): boolean {
    return this._data.dirty
  }

  set dirty(dirty: boolean) {
    this._data.dirty = dirty
  }

  get name(): string {
    return this._data.name
  }

  set name(name: string) {
    this._data.name = name
  }

  get logoURL(): string {
    return this._data.logo_url
  }

  set logoURL(logoURL: string) {
    this._data.logo_url = logoURL
  }

  get faviconURL(): string {
    return this._data.favicon_url
  }

  set faviconURL(faviconURL: string) {
    this._data.favicon_url = faviconURL
  }

  get website(): string {
    return this._data.website
  }

  set website(website: string) {
    this._data.website = website
  }

  get address(): string {
    return this._data.address
  }

  set address(address: string) {
    this._data.address = address
  }

  get city(): string {
    return this._data.city
  }

  set city(city: string) {
    this._data.city = city
  }

  get state(): string {
    return this._data.state
  }

  set state(state: string) {
    this._data.state = state
  }

  get postalCode(): string {
    return this._data.postal_code
  }

  set postalCode(postalCode: string) {
    this._data.postal_code = postalCode
  }

  get country(): string {
    return this._data.country
  }

  set country(country: string) {
    this._data.country = country
  }

  get currency(): string {
    return this._data.currency
  }

  set currency(currency: string) {
    this._data.currency = currency
  }

  get timezone(): string {
    return this._data.timezone
  }

  set timezone(timezone: string) {
    this._data.timezone = timezone
  }

  get phone(): string {
    return this._data.phone
  }

  set phone(phone: string) {
    this._data.phone = phone
  }

  get email(): string {
    return this._data.email
  }

  set email(email: string) {
    this._data.email = email
  }

  get localeString(): string {
    return this._data.locale_string
  }

  set localeString(localeString: string) {
    this._data.locale_string = localeString
  }

  get plan(): number {
    return this._data.plan
  }

  set plan(plan: number) {
    this._data.plan = plan
  }

  get subdomain(): string {
    return this._data.subdomain
  }

  set subdomain(subdomain: string) {
    this._data.subdomain = subdomain
  }

  get domain(): string {
    return this._data.domain
  }

  set domain(domain: string) {
    this._data.domain = domain
  }

  get promoterId(): number {
    return this._data.promoter_id
  }

  set promoterId(promoterId: number) {
    this._data.promoter_id = promoterId
  }

  get sparedomain(): string {
    return this._data.spare_domain
  }

  set sparedomain(sparedomain: string) {
    this._data.spare_domain = sparedomain
  }

  get status(): Status {
    return this._data['status']
  }

  set status(status: Status) {
    this._data['status'] = status
  }

  get stripeId(): string {
    return this._data['stripe_id']
  }

  set stripeId(stripeId: string) {
    this._data['stripe_id'] = stripeId
  }

  get stripeSubscriptionId(): string {
    return this._data['stripe_subscription_id']
  }

  set stripeSubscriptionId(stripeSubscriptionId: string) {
    this._data['stripe_subscription_id'] = stripeSubscriptionId
  }

  get stripeConnectId(): string {
    return this._data['stripe_connect_id']
  }

  set stripeConnectId(stripeConnectId: string) {
    this._data['stripe_connect_id'] = stripeConnectId
  }

  get stripeConnectProcessed(): boolean {
    return this._data['stripe_connect_processed']
  }

  set stripeConnectProcessed(stripeConnectProcessed: boolean) {
    this._data['stripe_connect_processed'] = stripeConnectProcessed
  }

  set saasSettings(saasSettings: ISaasSettings) {
    this._data['saas_settings'] = saasSettings
  }

  get saasSettings(): ISaasSettings {
    return this._data['saas_settings']
  }

  // set isvSettings(isvSettings: IIsvSettings) {
  //   this._data['isv_settings'] = isvSettings
  // }

  // get isvSettings(): IIsvSettings {
  //   return this._data['isv_settings']
  // }

  get inIsvMode(): boolean {
    return this._data.in_isv_mode || false
  }

  set inIsvMode(inIsvMode: boolean) {
    this._data.in_isv_mode = inIsvMode
  }

  get hasLcEmail(): boolean {
    return this._data.has_lc_email || false
  }

  set hasLcEmail(hasLcEmail: boolean) {
    this._data.has_lc_email = hasLcEmail
  }

  get inPureIsvMode(): boolean {
    // To Hide Twilio Tab from agency settings
    return this._data.in_pure_isv_mode || false
  }

  set inPureIsvMode(inPureIsvMode: boolean) {
    this._data.in_pure_isv_mode = inPureIsvMode
  }

  get stripeActivePlan(): string {
    return this._data['stripe_active_plan']
  }

  set stripeActivePlan(stripeActivePlan: string) {
    this._data['stripe_active_plan'] = stripeActivePlan
  }

  get dateInactivation(): moment.Moment {
    // set date inactivation to 1 year from now if it is not set
    return this._data['date_inactivation']
      ? moment(this._data['date_inactivation'].toMillis())
      : moment(+new Date() + (1000 * 60 * 60 * 24 * 365))
  }

  set dateInactivation(dateInactivation: moment.Moment) {
    this._data['date_inactivation'] = dateInactivation.toDate()
  }

  get affiliatePixelId(): string {
    return this._data['affiliate_pixel_id']
  }

  set affiliatePixelId(affiliatePixelId: string) {
    this._data['affiliate_pixel_id'] = affiliatePixelId
  }

  get cardLast4(): string {
    return this._data['card_last_4']
  }

  set cardLast4(cardLast4: string) {
    this._data['card_last_4'] = cardLast4
  }

  get cardBrand(): string {
    return this._data['card_brand']
  }

  set cardBrand(cardBrand: string) {
    this._data['card_brand'] = cardBrand
  }

  get customerType(): CustomerType {
    return this._data.customer_type
  }

  set customerType(customerType: CustomerType) {
    this._data.customer_type = customerType
  }

  get source(): Source {
    return this._data.source
  }

  set source(source: Source) {
    this._data.source = source
  }

  get referralId(): string {
    return this._data.referral_id
  }

  set referralId(referralId: string) {
    this._data.referral_id = referralId
  }

  get privacyPolicy(): string {
    return this._data.privacy_policy
  }

  set privacyPolicy(privacy_policy: string) {
    this._data.privacy_policy = privacy_policy
  }
  get services(): string {
    return this._data.services
  }

  get customJavascript(): string {
    return this._data['custom_javascript']
  }

  set customJavascript(custom_javascript: string) {
    this._data['custom_javascript'] = custom_javascript
  }

  get customCSS(): string {
    return this._data['custom_css']
  }

  set customCSS(custom_css: string) {
    this._data['custom_css'] = custom_css
  }

  get funnelAvailability(): boolean {
    return this._data['funnel_availability']
  }

  set funnelAvailability(funnel_availability: boolean) {
    this._data['funnel_availability'] = funnel_availability
  }

  get onboardingInfo(): { [key: string]: any } {
    if (this._data.onboarding_info === undefined)
      this._data.onboarding_info = {}
    return this._data.onboarding_info
  }

  set onboardingInfo(onboardingInfo: { [key: string]: any }) {
    this._data.onboarding_info = onboardingInfo
  }

  get hipaaCompliance(): boolean {
    return this._data['hipaa_compliance'] || false
  }

  set hipaaCompliance(hipaaCompliance: boolean) {
    this._data['hipaa_compliance'] = hipaaCompliance || false
  }

  get hipaaStatus(): string {
    return this._data['hipaa_status']
  }

  set hipaaStatus(hipaaStatus: string) {
    this._data['hipaa_status'] = hipaaStatus
  }

  get businessNiche(): string {
    return this._data['business_niche']
  }

  set businessNiche(businessNiche: string) {
    this._data['business_niche'] = businessNiche
  }


  get businessCategory(): string {
    return this._data['business_category']
  }

  get businessAffinityGroup(): string {
    return this._data['business_affinity_group']
  }

  get agencyPrimaryUse(): string {
    return this._data['agency_primary_use']
  }

  set agencyPrimaryUse(agencyPrimaryUse: string) {
    this._data['agency_primary_use'] = agencyPrimaryUse
  }

  set premiumUpgraded(premiumUpgraded: boolean) {
    this._data['premium_upgraded'] = premiumUpgraded
  }

  get premiumUpgraded(): boolean {
    return this._data['premium_upgraded']
  }

  get premiumUpgradeStatus(): string {
    // 'Under Review' || 'Subscribed' || 'inactive'
    return this._data['premium_upgrade_status']
  }

  set premiumUpgradeStatus(premiumUpgradeStatus: string) {
    this._data['premium_upgrade_status'] = premiumUpgradeStatus
  }

  /* Whitelabel-app-service specific */
  get whitelabelAppEnabled(): boolean {
    return this._data['whitelabel_app_enabled'] || false
  }

  set whitelabelAppEnabled(whitelabelAppEnabled: boolean) {
    this._data['whitelabel_app_enabled'] = whitelabelAppEnabled || false
  }

  get whitelabelAppStatus(): string {
    return this._data['whitelabel_app_status']
  }

  set whitelabelAppStatus(whitelabelAppStatus: string) {
    this._data['whitelabel_app_status'] = whitelabelAppStatus
  }

  get whitelabelAppPlan(): string {
    return this._data['whitelabel_app_plan']
  }

  set whitelabelAppPlan(whitelabelAppPlan: string) {
    this._data['whitelabel_app_plan'] = whitelabelAppPlan
  }

  get whitelabelAppOrderId(): string {
    // Agency_order_id of the paid request.
    return this._data['whitelabel_app_order_id']
  }

  set whitelabelAppOrderId(whitelabelAppOrderId: string) {
    this._data['whitelabel_app_order_id'] = whitelabelAppOrderId
  }
  /* Agency-jumpstart-service specific */
  get jumpstartSupported(): Date {
    return this._data['jumpstart_supported']
  }

  set jumpstartSupported(jumpstartSupported: Date) {
    this._data['jumpstart_supported'] = jumpstartSupported
  }

  get callTrackingEnabled(): boolean {
    return this._data.call_tracking_enabled || false
  }

  get marketplaceEnabled(): boolean {
    return this._data.marketplace_enabled
  }

  set marketplaceEnabled(marketplaceEnabled: boolean) {
    this._data.marketplace_enabled = marketplaceEnabled
  }

  get betaServicesEnabled(): boolean {
    return this._data.beta_services_enabled
  }

  set betaServicesEnabled(betaServicesEnabled: boolean) {
    this._data.beta_services_enabled = betaServicesEnabled
  }

  get twilioFreeCredits(): number {
    return this._data.twilio_free_credits
  }

  set twilioFreeCredits(twilioFreeCredits: number) {
    this._data.twilio_free_credits = twilioFreeCredits
  }

  get twilioTrialMode(): boolean {
    return this._data.twilio_trial_mode
  }

  set twilioTrialMode(twilioTrialMode: boolean) {
    this._data.twilio_trial_mode = twilioTrialMode
  }
  get defaultEmailService(): string {
    return this._data.default_email_service
  }

  get upgradeAlertDismissed(): moment.Moment {
    try {
      return moment(this._data['upgrade_alert_dismissed'] ? this._data['upgrade_alert_dismissed'] : 1000)
    } catch (error) {
      return
    }
  }

  set upgradeAlertDismissed(upgradeAlertDismissedDate: moment.Moment) {
    this._data['upgrade_alert_dismissed'] = upgradeAlertDismissedDate.toDate()
  }

  set defaultEmailService(defaultEmailService: string) {
    this._data.default_email_service = defaultEmailService
  }

  get stripeSecretKey(): string {
    return this._data.stripe_key
  }

  set stripeSecretKey(stripeSecretKey: string) {
    this._data.stripe_key = stripeSecretKey
  }

  get yextReseller(): YextAgencySettings {
    if (!this._data.reseller) {
      this._data.reseller = {}
    }
    if (this._data.reseller.yext) {
      return this._data.reseller.yext
    }
    this._data.reseller.yext = {
      hl_price: 30,
      agency_price: 49,
      enabled: false,
    }
    return this._data.reseller.yext
  }

  set yextReseller(yextSetting: YextAgencySettings)  {
    if (!this._data.reseller) {
      this._data.reseller = {}
    }
    this._data.reseller.yext = { ...yextSetting, hl_price: 30 } // hl_price:30 is important because client and try to override although we are only using it display purpose
  }


  get theme(): string {
    return this._data.theme
  }

  set theme(theme: string) {
    this._data.theme = theme
  }

  get elizaEnabled(): boolean {
    return this._data.eliza_enabled
  }

  set elizaEnabled(elizaEnabled: boolean) {
    this._data.eliza_enabled = elizaEnabled
  }

  get elizaFirstLocationEnabled(): boolean {
    return this._data.eliza_first_location_enabled
  }

  set elizaFirstLocationEnabled(elizaFirstLocationEnabled: boolean) {
    this._data.eliza_first_location_enabled = elizaFirstLocationEnabled
  }

  get allowBetaAccess(): AllowBetaAccess {
    return this._data.allowBetaAccess
  }

  set allowBetaAccess(allowBetaAccess: AllowBetaAccess) {
    this._data.allowBetaAccess = allowBetaAccess
  }

  get billingInfo(): BillingInfo {
    if (this._data.billing_info === undefined) this._data.billing_info = {}
    return this._data.billing_info
  }

  set billingInfo(billingInfo: BillingInfo) {
    this._data.billing_info = billingInfo
  }

  get termsOfServiceVersion(): string {
    return this._data.terms_of_service_version
  }

  set termsOfServiceVersion(terms_of_service_version: string) {
    this._data.terms_of_service_version = terms_of_service_version
  }

  get termsOfService(): boolean {
    return this._data.terms_of_service
  }

  set termsOfService(terms_of_service: boolean) {
    this._data.terms_of_service = terms_of_service
  }

  get termsOfServiceAcceptedBy(): string {
    return this._data.terms_of_service_accepted_by
  }

  set termsOfServiceAcceptedBy(userId: string) {
    this._data.terms_of_service_accepted_by = userId
  }

  get termsOfServiceAcceptedDate(): moment.Moment {
    try {
      return moment(
        this._data['terms_of_service_accepted_date']
          ? this._data['terms_of_service_accepted_date'].toMillis()
          : 1000
      )
    } catch (err) {
      return
    }
  }

  set termsOfServiceAcceptedDate(dateApproved: moment.Moment) {
    this._data.terms_of_service_accepted_date = dateApproved.toDate()
  }

  get isReselling(): boolean {
    return this._data.is_reselling
  }

  set isReselling(reselling: boolean) {
    this._data.is_reselling = reselling
  }

  get isEnterprise(): boolean {
    return this._data.is_enterprise
  }

  set isEnterprise(isEnterprise: boolean) {
    this._data.is_enterprise = isEnterprise
  }

  get hideLaunchpad(): boolean {
    return this._data.hide_launchpad
  }

  set hideLaunchpad(hideLaunchpad: boolean) {
    this._data.hide_launchpad = hideLaunchpad
  }

  get experianceNpsEnabled(): boolean {
    return this._data.experiance_nps_enabled === undefined
      ? true
      : this._data.experiance_nps_enabled
  }

  set experianceNpsEnabled(experianceNpsEnabled: boolean) {
    this._data.experiance_nps_enabled = experianceNpsEnabled
  }

  get experianceGuidesEnabled(): boolean {
    return this._data.experiance_guides_enabled === undefined
      ? true
      : this._data.experiance_guides_enabled
  }

  set experianceGuidesEnabled(experianceGuidesEnabled: boolean) {
    this._data.experiance_guides_enabled = experianceGuidesEnabled
  }

  get experianceReportsEnabled(): boolean {
    return this._data.experiance_reports_enabled === undefined
      ? true
      : this._data.experiance_reports_enabled
  }

  set experianceReportsEnabled(experianceReportsEnabled: boolean) {
    this._data.experiance_reports_enabled = experianceReportsEnabled
  }


  get betaFeaturesEnabled(): boolean {
    return this._data.beta_features_enabled === undefined
      ? true
      : this._data.beta_features_enabled
  }

  set betaFeaturesEnabled(enabled: boolean) {
    this._data.beta_features_enabled = enabled
  }

  get sideBarNotificationType(): string | undefined {
    if (this._data.sidebar_v2_notification_type) {
      return this._data.sidebar_v2_notification_type
    } else if (this._data.sidebar_enable) {
      return 'optional'
    }
    return undefined
  }

  set sideBarNotificationType(type: string) {
    this._data.sidebar_v2_notification_type = type
  }

  get whitelabelAppLinks(): WhitelabelAppLinks {
    return this._data.whitelabel_app_links
      ? this._data.whitelabel_app_links
      : {}
  }

  set whitelabelAppLinks(appLinks: WhitelabelAppLinks) {
    this._data.whitelabel_app_links = appLinks
  }

  get whatsapp(): { [key: string]: any } {
    if (!this._data.whatsapp) {
      this._data.whatsapp = {
        enable: false,
        number_subscription: {
          locations: {},
          total_quantity: 0
        }
      };
    }
    return this._data.whatsapp;
  }

  set whatsapp(whatsappSettings: { [key: string]: any }) {
    if (!this._data.whatsapp) {
      this._data.whatsapp = {
        enable: false,
        number_subscription: {
          locations: {},
          total_quantity: 0
        }
      };
    }
    this._data.whatsapp = { ...this._data.whatsapp, ...whatsappSettings};
  }

  get newImportFlow(): boolean {
    return this._data.new_import_flow
  }

  set newImportFlow(flag: boolean) {
    this._data.new_import_flow = flag
  }

  get relationshipNumber(): String {
    return this._data.relationship_number
  }

  set relationshipNumber(relationshipNumber: String) {
    this._data.relationship_number = relationshipNumber
  }

  get enableDepreciatedFeatures(): boolean {
    return this._data.enable_depreciated
  }

  set enableDepreciatedFeatures(enable: boolean) {
    this._data.enable_depreciated = enable
  }

  get paUsage(): PaUsageValues[] {
   return this._data.pa_usage || []
  }

  set paUsage(values: PaUsageValues[]) {
    this._data.pa_usage = values
  }

  get showSurveyModal() {
    return this.dateAdded?.isBefore(moment().subtract(60, 'days')) && this.paUsage.length === 0
  }

  get privacyPolicyVersion(): string {
    return this._data.privacy_policy_verison
  }

  set privacyPolicyVersion(privacy_policy_verison: string) {
    this._data.privacy_policy_verison = privacy_policy_verison
  }

  get privacyPolicyAcceptedBy(): string {
    return this._data.privacy_policy_accepted_by
  }

  set privacyPolicyAcceptedBy(userId: string) {
    this._data.privacy_policy_accepted_by = userId
  }

  get privacyPolicyAcceptedDate(): moment.Moment {
    try {
      return moment(
        this._data['privacy_policy_accepted_date']
          ? this._data['privacy_policy_accepted_date'].toMillis()
          : 1000
      )
    } catch (err) {
      return
    }
  }

  set privacyPolicyAcceptedDate(dateApproved: moment.Moment) {
    this._data.privacy_policy_accepted_date = dateApproved.toDate()
  }

  get affiliatePolicyVersion(): string {
    return this._data.affiliate_policy_verison
  }

  set affiliatePolicyVersion(affiliate_policy_verison: string) {
    this._data.affiliate_policy_verison = affiliate_policy_verison
  }

  get affiliatePolicyAcceptedBy(): string {
    return this._data.affiliate_policy_accepted_by
  }

  set affiliatePolicyAcceptedBy(userId: string) {
    this._data.affiliate_policy_accepted_by = userId
  }

  get affiliatePolicyAcceptedDate(): moment.Moment {
    try {
      return moment(
        this._data['privacy_policy_accepted_date']
          ? this._data['privacy_policy_accepted_date'].toMillis()
          : 1000
      )
    } catch (err) {
      return
    }
  }

  set affiliatePolicyAcceptedDate(dateApproved: moment.Moment) {
    this._data.affiliate_policy_accepted_date = dateApproved.toDate()
  }

  get upgradeEnabledForClients():boolean{
    return this._data.upgrade_enabled_for_clients;
  }

  set upgradeEnabledForClients(upgradeFlag:boolean){
    this._data.upgrade_enabled_for_clients = upgradeFlag;
  }

  get showMobileAppToClients(): boolean {
    return this._data['show_mobile_app_to_clients']
  }

  set showMobileAppToClients(value: boolean) {
    this._data['show_mobile_app_to_clients'] = value
  }

  get cancelEnabledForClients():boolean{
    return this._data.cancel_enabled_for_clients;
  }

  set cancelEnabledForClients(cancelFlag:boolean){
    this._data.cancel_enabled_for_clients = cancelFlag;
  }

  get autoSuspendEnabled():boolean{
    return this._data.auto_suspend_enabled;
  }

  set autoSuspendEnabled(autoSuspendFlag:boolean){
    this._data.auto_suspend_enabled = autoSuspendFlag;
  }

  get bulkTwilioMigrationRequested():boolean{
    return this._data.bulk_twilio_migration_requested;
  }

  set bulkTwilioMigrationRequested(bulkTwilioMigrationRequested:boolean){
    this._data.bulk_twilio_migration_requested = bulkTwilioMigrationRequested;
  }

  get securityRiskBlock(): boolean {
    return this._data.security_risk_block;
  }

  set securityRiskBlock(block: boolean) {
    this._data.security_risk_block = block
  }

  set locationCount(locationCount: number) {
    this._data.location_count = locationCount
  }

  get locationCount() {
    return this._data.location_count
  }

  get isSandboxAccount() {
    return this._data.is_sandbox_account
  }

  set isSandboxAccount(value: boolean) {
    this._data.is_sandbox_account = value
  }
  get sandboxAccountDetails() {
    if (this._data.sandbox_account_details === undefined) this._data.sandbox_account_details = {}
    return this._data.sandbox_account_details
  }

  set sandboxAccountDetails(sandboxAccountDetails: MarketplaceSandbox) {
    this._data.sandbox_account_details = sandboxAccountDetails
  }

  get blockFeatures(): BlockFeatures {
    return this._data.block_features
  }

  set blockFeatures(value: BlockFeatures) {
    this._data.block_features = value
  }

  get experienceNpsEnabled(): boolean {
    return this._data['experiance_nps_enabled']
  }

  set experienceNpsEnabled(experienceNpsEnabled: boolean) {
    this._data['experiance_nps_enabled'] = experienceNpsEnabled
  }
  get experienceGuidesEnabled(): boolean {
    return this._data['experiance_guides_enabled']
  }

  set experienceGuidesEnabled(experienceGuidesEnabled: boolean) {
    this._data['experiance_guides_enabled'] = experienceGuidesEnabled
  }
  get experienceReportsEnabled(): boolean {
    return this._data['experiance_reports_enabled']
  }

  set experienceReportsEnabled(experienceReportsEnabled: boolean) {
    this._data['experiance_reports_enabled'] = experienceReportsEnabled
  }

  get yextAgencyPrice(): number {
    if (this._data?.reseller?.yext?.agency_price) {
      return this._data?.reseller?.yext?.agency_price
    } else {
      return
    }
  }

  set yextAgencyPrice(yextAgencyPrice: number) {
    if (this._data?.reseller) {
      if (this._data?.reseller?.yext) {
        this._data.reseller.yext['agency_price'] = yextAgencyPrice
      } else {
        this._data.reseller['yext'] = {}
        this._data.reseller['yext']['agency_price'] = yextAgencyPrice
      }
      this._data.reseller.yext.agency_price = yextAgencyPrice
    } else {
      this._data['reseller'] = {}
      this._data['reseller']['yext'] = {}
      this._data['reseller']['yext']['agency_price'] = yextAgencyPrice
    }
  }

  get yextSaasOffer(): YextSaasOffer {
    if (this._data?.reseller?.yext?.saas_offer) {
      return this._data?.reseller?.yext?.saas_offer
    } else {
      return
    }
  }

  set yextSaasOffer(yextSaasOffer: YextSaasOffer) {
    if (this._data?.reseller) {
      if (this._data?.reseller?.yext) {
        this._data.reseller.yext['saas_offer'] = yextSaasOffer
      } else {
        this._data.reseller['yext'] = {}
        this._data.reseller['yext']['saas_offer'] = yextSaasOffer
      }
      this._data.reseller.yext.saas_offer = yextSaasOffer
    } else {
      this._data['reseller'] = {}
      this._data['reseller']['yext'] = {}
      this._data['reseller']['yext']['saas_offer'] = yextSaasOffer
    }
  }

  get activeStatusDetails(): ActiveStatusDetails {
    if (this._data.active_status_details === undefined) this._data.active_status_details = {}
    return this._data.active_status_details
  }

  set activeStatusDetails(activeStatusDetails: ActiveStatusDetails) {
    activeStatusDetails = Utils.convertToSnakeCaseObject(activeStatusDetails)
    this._data.active_status_details = activeStatusDetails
  }

  get inactiveSessionTimeLimit(): string {
    return this._data.inactive_session_timelimit
  }

  set inactiveSessionTimeLimit(inactiveSessionTimeLimit: string) {
    this._data.inactive_session_timelimit = inactiveSessionTimeLimit
  }
}
